'use client'
import React, { FC } from 'react'
import ReactPlayer from 'react-player'
import { BaseModal } from '@/components/ui/common/modal'
import { CommonVideoSkeleton } from '@/components/ui/common/CommonVideoSkeleton'
import { IVideoRecommendation } from '@/hooks/useRecommendationVideoRecommendations'
import { useNoScroll } from '@/hooks/useNoScroll'
import styles from '@/components/ui/common/lessons/styles/index.module.css'

interface Props {
  isOpen: boolean
  onClose: () => void
  videos: IVideoRecommendation[]
  topicTitle?: string
  isLoading?: boolean
}

export const RecommendationVideoModal: FC<Props> = ({
  isOpen,
  onClose,
  videos,
  topicTitle,
  isLoading = false,
}) => {
  useNoScroll(isOpen)

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      withCloseButton
      content={
        <div className="w-4xl pt-2 flex flex-col gap-8">
          {/* Header - identical to ActiveModeLesson */}
          <div className="w-full px-4 flex flex-col gap-1 text-v8-mut">
            {/* Title */}
            <h2 className="text-lg">Still unsure about something?</h2>
            {/* Subtitle */}
            <p className="text-sm">
              {`These videos can help clarify and deepen your knowledge${
                topicTitle ? ' in ' + topicTitle : ''
              }.`}
            </p>
          </div>

          {/* Body - identical structure to ActiveModeLesson */}
          <div className="py-2 pl-2 pr-1 sm:py-4 sm:pl-4 sm:pr-2 bg-v8-blue/10 rounded-md">
            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-v8-blue"></div>
                <span className="ml-3 text-sm text-v8-mut">
                  Loading video recommendations...
                </span>
              </div>
            )}

            {/* No Videos State */}
            {!isLoading && videos.length === 0 && (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <div className="text-4xl mb-2">🎥</div>
                  <p className="text-sm text-v8-mut">
                    No video recommendations found
                  </p>
                  <p className="text-xs text-v8-mut mt-1">
                    Try again later or contact support
                  </p>
                </div>
              </div>
            )}

            {/* Videos Grid - identical to ActiveModeLesson */}
            {!isLoading && videos.length > 0 && (
              <ul className="pr-1 sm:pr-2 max-h-[400px] grid grid-cols-1 gap-6 md:grid-cols-2 thinScrollbar overflow-y-auto">
                {videos.map((video, index) => (
                  <li
                    key={index + `_${video.videoUrl}`}
                    className="w-full flex flex-col gap-2"
                  >
                    {/* Video Title */}
                    <p className="text-start text-xs text-v8-mut text-nowrap text-ellipsis overflow-hidden">
                      {video.name || ''}
                    </p>

                    {/* Video Player */}
                    <div className={`w-full ${styles.videoPlayerContainer}`}>
                      <ReactPlayer
                        className={`${styles.videoPlayer}`}
                        width="100%"
                        height="100%"
                        url={video.videoUrl}
                        controls
                        playing={false}
                        muted={false}
                        fallback={<CommonVideoSkeleton useSpinner={false} />}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      }
      bodyAdditionalClassName="max-h-[calc(100vh-var(--student-header-height))]"
      customZIndexClass="z-[655]"
    />
  )
}

export default RecommendationVideoModal
